/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import type { ComponentProps } from 'react'
import { forwardRef } from 'react'
import type { RegisterOptions } from 'react-hook-form'
import { Controller, useFormContext, useFormState } from 'react-hook-form'

import { cn } from '../../../lib/utils'
import { Field, FieldDescription, FieldError, FieldLabel } from '../../ui/field'
import { Slider } from '../../ui/slider'

export type SliderFormFieldProps = {
  defaultValue?: number
  disabled?: boolean
  fieldProps?: ComponentProps<typeof Field>
  helperText?: string
  label?: string
  name: string
  onValueChange?: (value: number[]) => void
  onValueChangeEnd?: (value: number[]) => void
  required?: boolean
  rules?: RegisterOptions
  sliderProps?: Omit<
    ComponentProps<typeof Slider>,
    'defaultValue' | 'disabled' | 'onValueChange' | 'onValueCommitted' | 'value'
  >
}

export const SliderFormField = forwardRef<HTMLDivElement, SliderFormFieldProps>((props, forwardedRef) => {
  const { control } = useFormContext()
  const { errors } = useFormState({ control, name: props.name })

  const error = errors[props.name]
  const invalid = !!error

  return (
    <Field {...props.fieldProps} data-invalid={invalid}>
      {props.label !== undefined && (
        <FieldLabel className="flex items-center gap-1">
          {props.label}
          {props.required ? (
            <span aria-hidden className="text-destructive">
              *
            </span>
          ) : null}
        </FieldLabel>
      )}
      <Controller
        control={control}
        name={props.name}
        render={({ field }) => {
          let normalizedValue: number[]
          if (Array.isArray(field.value)) {
            normalizedValue = field.value
          } else if (field.value !== undefined && field.value !== null && field.value !== '') {
            normalizedValue = [Number(field.value)]
          } else if (props.defaultValue !== undefined) {
            normalizedValue = [props.defaultValue]
          } else {
            normalizedValue = [0]
          }

          return (
            <div className={cn('flex w-full gap-4')} ref={forwardedRef}>
              <Slider
                {...props.sliderProps}
                aria-invalid={invalid}
                disabled={props.disabled}
                onValueChange={(value) => {
                  const next = Array.isArray(value) ? [...value] : [value as number]
                  field.onChange(next)
                  props.onValueChange?.(next)
                }}
                onValueCommitted={(value) => {
                  const next = Array.isArray(value) ? [...value] : [value as number]
                  props.onValueChangeEnd?.(next)
                  field.onBlur()
                }}
                value={normalizedValue}
              />
            </div>
          )
        }}
        rules={props.rules}
      />
      {props.helperText !== undefined ? <FieldDescription>{props.helperText}</FieldDescription> : null}
      {error?.message ? <FieldError>{String(error.message)}</FieldError> : null}
    </Field>
  )
})

SliderFormField.displayName = 'SliderFormField'
